import type { FC } from 'react';
import React, { useState } from 'react';

import useDownloadCsvPolling from 'Components/DownloadCSV/useDownloadCsvPolling/useDownloadCsvPolling';
import { QueryCoreStatus } from 'Types/queryCoreStatus';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import DownloadCSVButton from './DownloadCSVButton/DownloadCSVButton';
import { DownloadCSVButtonWithPopover } from './DownloadCSVButtonWithPopover/DownloadCSVButtonWithPopover';
import { useDownloadCsvAnalytics } from './useDownloadCsvAnalytics/useDownloadCsvAnalytics';
import { useDownloadCsvToast } from './useDownloadCsvToast/useDownloadCsvToast';
import { pollingPropsDefaults } from './DownloadCSV.consts';
import { downloadCsvPollingMethod, downloadStaticCsv } from './downloadCSV.helpers';
import type { DownloadCSVProps } from './DownloadCSV.types';
import { PollingState } from './DownloadCSV.types';

const DownloadCSV: FC<DownloadCSVProps> = ({
	className,
	cypressId,
	downloadButtonLabel,
	dynamicDownloadOptions,
	handlers,
	isDisabled: isDisabledFromProps,
	isDynamic,
	pollingProps = pollingPropsDefaults,
	queryStatus,
	staticDownloadUrl,
	tooltip,
}) => {
	const { startPolling, state } = useDownloadCsvPolling(pollingProps);
	const [isStaticFileDownloading, setIsStaticFileDownloading] = useState(false);
	const isBulkBiddingCsvEnabled = useFeatureToggle(FEATURES.bulkBiddingCsv);

	useDownloadCsvToast(state);
	useDownloadCsvAnalytics({ handlers, state });

	const isPolling = state === PollingState.Polling;
	const isDisabledStatus = queryStatus === QueryCoreStatus.Loading || queryStatus === QueryCoreStatus.Error;
	const isDisabled = isDisabledFromProps || isDisabledStatus;
	const isLoading = isPolling || isStaticFileDownloading;

	const downloadButtonHandler = isDynamic ? handlers?.onPopoverDownloadButtonClick : handlers?.onDownloadButtonClick;

	const onDownloadButtonClick = async (selected?: string) => {
		if (isPolling) {
			return;
		}

		const selectedItem = dynamicDownloadOptions?.items?.find(({ id }) => id === selected);

		downloadButtonHandler?.(selectedItem?.label);

		const staticUrl = staticDownloadUrl && !isDynamic ? staticDownloadUrl : selectedItem?.staticDownloadUrl;

		if (staticUrl) {
			const { filenameTitle } = pollingProps;
			await downloadStaticCsv({
				filenameTitle,
				setIsStaticFileDownloading,
				shouldAppendBulkBiddingColumn: isBulkBiddingCsvEnabled,
				staticUrl,
			});
			return;
		}

		await downloadCsvPollingMethod(startPolling);
	};

	const commonButtonProps = {
		className,
		cypressId,
		downloadButtonLabel,
		isDisabled,
		isPolling,
		tooltip,
	};

	if (isDynamic && dynamicDownloadOptions) {
		return (
			<DownloadCSVButtonWithPopover
				{...commonButtonProps}
				dynamicDownloadOptions={{
					...dynamicDownloadOptions,
					onDownloadButtonClick,
				}}
				isLoading={isLoading}
				onClick={handlers?.onDynamicDownloadButtonClick}
				onHover={handlers?.onDynamicDownloadButtonHover}
			/>
		);
	}

	return (
		<DownloadCSVButton
			{...commonButtonProps}
			isLoading={isLoading}
			onClick={onDownloadButtonClick}
			onHover={handlers?.onDownloadButtonHover}
		/>
	);
};

export { DownloadCSV };
